import useAuthStore from "@/stores/useAuthStore";
import { CrewSummaryWithPlayerIds, RunAward, RunSummary, SeasonSummaryWithPlayerIds, StatusSummaryWithPlayer, SubRequestSummary } from "@shared/lib/database/query.types";
import { isBefore } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, Ref } from "vue";
import useNow from "./useNow";
import useRunStatus from "./useRunStatus";

export default function (run: Ref<RunSummary & {
  crews: Array<CrewSummaryWithPlayerIds>,
  statuses: Array<StatusSummaryWithPlayer>,
  sub_requests: Array<SubRequestSummary>,
  season: SeasonSummaryWithPlayerIds | null,
  awards: Array<RunAward>,
}>) {
  const {
    hasRunStarted,
    hasRunEnded,
    isRunActive,
  } = useRunStatus(run);

  const {
    now,
  } = useNow();

  const {
    currentPlayer,
  } = storeToRefs(useAuthStore());

  const isCrewPlayer = computed(() => {
    if (!currentPlayer.value) return false;

    return run.value.crews.some((crew) => crew.players.find((player) => player.id === currentPlayer.value?.id));
  });

  const isSeasonRegular = computed(() => {
    return !!run.value?.season?.players.find((player) => player.id === currentPlayer.value?.id);
  });

  const currentPlayerStatus = computed(() => {
    if (!currentPlayer.value) return null;

    return run.value?.statuses.find((status) => status.player_id === currentPlayer.value?.id) || null;
  });

  const inCount = computed(() => {
    return run.value.statuses.filter((status) => status.probability === 1).length;
  });

  const isAtCapacity = computed(() => {
    return !!run.value.max_player_count && inCount.value >= run.value.max_player_count;
  });

  const currentPlayerHasSpot = computed(() => {
    return currentPlayerStatus.value?.probability === 1;
  });

  const isInvitedPlayer = computed(() => {
    return run.value.sub_requests.some((subRequest) => subRequest.player_id === currentPlayer.value?.id && !!subRequest.accepted_at);
  });

  const canInteractWithRun = computed(() => {
    return !run.value.canceled_at
      && (!hasRunStarted.value || isRunActive.value);
  });

  const canUpdateStatus = computed(() => {
    return !!currentPlayer.value
      && canInteractWithRun.value
      && (
        isCrewPlayer.value && !run.value.season
        || isSeasonRegular.value
        || !!currentPlayerStatus.value
        || isInvitedPlayer.value
      );
  });

  const canJoinRun = computed(() => {
    return canUpdateStatus.value && (!isAtCapacity.value || currentPlayerHasSpot.value);
  });

  const canReviewRun = computed(() => {
    return !!currentPlayerStatus.value
      && currentPlayerStatus.value.probability > 0
      && hasRunEnded.value
      && run.value.awards.some((award) => award.voting_closed_at === null);
  });

  const isRunUnlocked = computed(() => {
    return !run.value.rsvp_at || !isBefore(now.value, run.value.rsvp_at);
  });

  return {
    currentPlayerStatus,
    canUpdateStatus,
    canJoinRun,
    isAtCapacity,
    canInteractWithRun,
    canReviewRun,
    isCrewPlayer,
    isSeasonRegular,
    isRunUnlocked,
  };
}
